import { LOCALE_SEARCH_PARAM } from "@/locale";

import { removeGoogleAdsClickIdsFromSearchParams } from "./googleAdsClickId";
import { LINKEDIN_ADS_CLICK_ID_QUERY_PARAM } from "./linkedInAdsClickId";
import { META_ADS_CLICK_ID_QUERY_PARAM } from "./metaAdsClickId";
import { REDDIT_ADS_CLICK_ID_QUERY_PARAM } from "./redditAdsClickId";

export const AD_ACCOUNT_QUERY_PARAM = "act";
export const AD_ACCOUNT_ACCESS_CHANGED_RECOVERY_REASON = "account_access_changed";
export const AD_ACCOUNT_ACCESS_CHANGED_RECOVERY_PATH = "/unavailable";
export const AD_ACCOUNT_RECOVERY_RETURN_TO_QUERY_PARAM = "returnTo";
const REACT_ROUTER_ROUTES_QUERY_PARAM = "_routes";

export function getAdAccountIdFromSearchParams(searchParams: URLSearchParams): string | undefined {
  return getHeaderSafeAdAccountId(searchParams.get(AD_ACCOUNT_QUERY_PARAM));
}

/**
 * Ad account IDs cross a request-header boundary in both SSR and browser
 * clients. Use the platform's header parser so malformed URL or cookie input
 * cannot make fetch throw before Sofa can return a normal API response.
 */
export function getHeaderSafeAdAccountId(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0 || hasFetchInvalidHeaderControlByte(value)) {
    return undefined;
  }

  try {
    const headers = new Headers({ "OpenAI-Ad-Account": value });
    return headers.get("OpenAI-Ad-Account") === value ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Node's Headers parser accepts some control bytes that undici rejects later
 * when fetch serializes the request. Reject the complete C0 range and DEL
 * before a selected account can cross the header boundary.
 */
function hasFetchInvalidHeaderControlByte(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit <= 0x1f || codeUnit === 0x7f) {
      return true;
    }
  }

  return false;
}

export function buildPathWithAdAccount(path: string | URL, adAccountId: string): string {
  const url = path instanceof URL ? new URL(path.toString()) : new URL(path, "http://placeholder");

  url.searchParams.set(AD_ACCOUNT_QUERY_PARAM, adAccountId);
  const search = url.searchParams.toString();
  return `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
}

export function buildAdAccountAccessChangedRecoveryPath(path: string | URL): string {
  const returnToUrl =
    path instanceof URL ? new URL(path.toString()) : new URL(path, "http://placeholder");
  if (returnToUrl.pathname.endsWith(".data")) {
    returnToUrl.pathname = withoutLoaderDataSuffix(returnToUrl.pathname);
  }
  const locale = returnToUrl.searchParams.get(LOCALE_SEARCH_PARAM);
  returnToUrl.searchParams.delete(AD_ACCOUNT_QUERY_PARAM);
  removeGoogleAdsClickIdsFromSearchParams(returnToUrl.searchParams);
  returnToUrl.searchParams.delete(META_ADS_CLICK_ID_QUERY_PARAM);
  returnToUrl.searchParams.delete(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
  returnToUrl.searchParams.delete(REDDIT_ADS_CLICK_ID_QUERY_PARAM);
  returnToUrl.searchParams.delete(REACT_ROUTER_ROUTES_QUERY_PARAM);

  const searchParams = new URLSearchParams({
    reason: AD_ACCOUNT_ACCESS_CHANGED_RECOVERY_REASON,
    [AD_ACCOUNT_RECOVERY_RETURN_TO_QUERY_PARAM]: toInternalPath(returnToUrl),
  });
  if (locale) {
    searchParams.set(LOCALE_SEARCH_PARAM, locale);
  }
  return AD_ACCOUNT_ACCESS_CHANGED_RECOVERY_PATH + "?" + searchParams.toString();
}

export function isAdAccountAccessChangedRecoveryPath(path: string | URL): boolean {
  const url = path instanceof URL ? path : new URL(path, "http://placeholder");
  const pathname = normalizeRoutePathname(url.pathname);
  return (
    pathname === AD_ACCOUNT_ACCESS_CHANGED_RECOVERY_PATH &&
    url.searchParams.get("reason") === AD_ACCOUNT_ACCESS_CHANGED_RECOVERY_REASON
  );
}

export function getAdAccountRecoveryReturnTo(searchParams: URLSearchParams): string | undefined {
  const rawReturnTo = searchParams.get(AD_ACCOUNT_RECOVERY_RETURN_TO_QUERY_PARAM);
  if (!rawReturnTo?.startsWith("/") || rawReturnTo.startsWith("//")) {
    return undefined;
  }

  try {
    const url = new URL(rawReturnTo, "http://placeholder");
    if (url.origin !== "http://placeholder" || isAdAccountAccessChangedRecoveryPath(url)) {
      return undefined;
    }

    url.pathname = withoutLoaderDataSuffix(url.pathname);
    url.searchParams.delete(AD_ACCOUNT_QUERY_PARAM);
    removeGoogleAdsClickIdsFromSearchParams(url.searchParams);
    url.searchParams.delete(META_ADS_CLICK_ID_QUERY_PARAM);
    url.searchParams.delete(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
    url.searchParams.delete(REDDIT_ADS_CLICK_ID_QUERY_PARAM);
    url.searchParams.delete(REACT_ROUTER_ROUTES_QUERY_PARAM);
    const returnTo = toInternalPath(url);
    return returnTo.startsWith("//") ? undefined : returnTo;
  } catch {
    return undefined;
  }
}

export function normalizeRoutePathname(pathname: string): string {
  const withoutTrailingSlashes = pathname.replace(/\/+$/, "") || "/";
  return withoutLoaderDataSuffix(withoutTrailingSlashes);
}

function toInternalPath(url: URL): string {
  const search = url.searchParams.toString();
  return url.pathname + (search ? "?" + search : "") + url.hash;
}

function withoutLoaderDataSuffix(pathname: string): string {
  return pathname.endsWith(".data") ? pathname.slice(0, -".data".length) : pathname;
}
